import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Truck, Package, ExternalLink, Loader2, Mail, ChevronRight,
  Sparkles, Clock, CheckCircle2, AlertCircle, Zap, RefreshCw,
} from "lucide-react";

const STATUS_LABELS = {
  pending_forward: { label: "Pending forward", color: "bg-[#D9923B]/15 text-[#A66811]", icon: Clock },
  forwarded: { label: "Forwarded", color: "bg-[#4A5D23]/15 text-[#4A5D23]", icon: Package },
  shipped: { label: "Shipped", color: "bg-[#C05A3C]/15 text-[#C05A3C]", icon: Truck },
  delivered: { label: "Delivered", color: "bg-[#2A3D36]/15 text-[#2A3D36]", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: AlertCircle },
};

const SupplierOrders = () => {
  const [orders, setOrders] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [autopilot, setAutopilot] = useState({ enabled: false, url_masked: null, secret_configured: false });
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shipFor, setShipFor] = useState(null); // {id, suggested_carrier}
  const [trackingInput, setTrackingInput] = useState("");
  const [carrierInput, setCarrierInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/admin/supplier-orders", { params });
      setOrders(data.orders || []);
      setByStatus(data.by_status || {});
      setAutopilot({
        enabled: !!data.autopilot_forward_enabled,
        url_masked: data.autopilot_forward_url_masked || null,
        secret_configured: !!data.autopilot_secret_configured,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load supplier orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const totalPending = byStatus.pending_forward || 0;
  const totalForwarded = byStatus.forwarded || 0;
  const totalShipped = byStatus.shipped || 0;

  const totalMargin = useMemo(() => {
    return orders.reduce((s, o) => {
      const paid = Number(o.buyer_paid || 0);
      const cost = Number(o.supplier_cost || 0);
      return paid > 0 && cost > 0 ? s + (paid - cost) : s;
    }, 0);
  }, [orders]);

  const submitShip = async (e) => {
    e?.preventDefault();
    if (!shipFor || !trackingInput.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/supplier-orders/${shipFor.id}/ship`, {
        tracking_number: trackingInput.trim(),
        carrier: carrierInput.trim(),
      });
      toast.success("Order shipped — buyer notified automatically");
      setShipFor(null);
      setTrackingInput("");
      setCarrierInput("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark shipped");
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/supplier-orders/${id}`, { status });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const retryForward = async (id) => {
    try {
      const { data } = await api.post(`/admin/supplier-orders/${id}/retry-forward`);
      if (data.ok) {
        toast.success("Order forwarded to webhook successfully");
      } else {
        toast.error(`Webhook returned ${data.status_code || "error"}: ${data.error || "unknown"}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry forward failed");
    }
  };

  const testWebhook = async () => {
    setTestingWebhook(true);
    try {
      const { data } = await api.post("/admin/autopilot-forward/test");
      if (data.ok) {
        toast.success(`Webhook test OK (HTTP ${data.status_code})`);
      } else {
        toast.error(`Webhook test failed: ${data.error || `HTTP ${data.status_code}`}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Webhook test failed");
    } finally {
      setTestingWebhook(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="supplier-orders-panel">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C05A3C]" />
            Autopilot · Supplier Orders
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Every paid curated order auto-creates a supplier-order. Forward to CJ / AliExpress, paste the tracking number — buyer gets auto-emailed.
          </p>
        </div>
      </header>

      {/* Autopilot webhook status — surfaces whether AUTOPILOT_FORWARD_URL is wired up. */}
      <div
        className={`flex items-start gap-3 p-3 rounded-xl border ${
          autopilot.enabled ? "bg-[#4A5D23]/8 border-[#4A5D23]/30" : "bg-[#D9923B]/10 border-[#D9923B]/40"
        }`}
        data-testid="autopilot-webhook-banner"
      >
        <Zap className={`w-5 h-5 shrink-0 mt-0.5 ${autopilot.enabled ? "text-[#4A5D23]" : "text-[#A66811]"}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#1A2024]">
            {autopilot.enabled ? "Webhook auto-forward active" : "Webhook auto-forward disabled"}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {autopilot.enabled ? (
              <>Orders auto-POST to <span className="font-mono">{autopilot.url_masked}</span>{autopilot.secret_configured ? " · HMAC secret enabled" : " · no secret set (orders not signed)"}</>
            ) : (
              <>Set <span className="font-mono">AUTOPILOT_FORWARD_URL</span> in <span className="font-mono">/app/backend/.env</span> (e.g. your Zapier / Make.com / n8n hook) and restart — buyer pays → order auto-forwards to the supplier.</>
            )}
          </div>
        </div>
        {autopilot.enabled && (
          <Button
            size="sm"
            variant="outline"
            onClick={testWebhook}
            disabled={testingWebhook}
            className="h-8 text-xs shrink-0"
            data-testid="autopilot-test-btn"
          >
            {testingWebhook ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Zap className="w-3 h-3 mr-1" /> Send test payload</>}
          </Button>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Pending forward" value={totalPending} accent="text-[#A66811]" active={filter === "pending_forward"} onClick={() => setFilter(filter === "pending_forward" ? null : "pending_forward")} testid="sup-kpi-pending" />
        <Kpi label="Forwarded" value={totalForwarded} accent="text-[#4A5D23]" active={filter === "forwarded"} onClick={() => setFilter(filter === "forwarded" ? null : "forwarded")} testid="sup-kpi-forwarded" />
        <Kpi label="Shipped" value={totalShipped} accent="text-[#C05A3C]" active={filter === "shipped"} onClick={() => setFilter(filter === "shipped" ? null : "shipped")} testid="sup-kpi-shipped" />
        <Kpi label="Margin (visible)" value={`£${totalMargin.toFixed(2)}`} accent="text-[#2A3D36]" testid="sup-kpi-margin" />
      </div>

      {filter && (
        <button
          type="button"
          onClick={() => setFilter(null)}
          className="text-xs text-[#C05A3C] underline"
          data-testid="sup-clear-filter"
        >
          Clear filter (showing {filter.replace("_", " ")})
        </button>
      )}

      {/* Table */}
      <div className="bg-white border border-black/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3EFE9] text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Product / Buyer</th>
                <th className="text-left px-4 py-3">Variant · Qty</th>
                <th className="text-left px-4 py-3">Margin</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-10 text-muted-foreground"><Loader2 className="inline w-4 h-4 mr-2 animate-spin" /> Loading…</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-muted-foreground" data-testid="sup-empty">
                  <Package className="w-10 h-10 mx-auto mb-2 text-muted-foreground/40" />
                  No supplier orders yet. As soon as a buyer pays for a curated dropship item, it appears here automatically.
                </td></tr>
              ) : (
                orders.map((o) => <Row key={o.supplier_order_id} order={o} setShipFor={setShipFor} updateStatus={updateStatus} retryForward={retryForward} autopilotEnabled={autopilot.enabled} />)
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ship dialog (inline form, not a modal — keeps the workflow flat) */}
      {shipFor && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !busy && setShipFor(null)}>
          <form
            onSubmit={submitShip}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl border border-black/10 max-w-lg w-full p-6 space-y-4 shadow-2xl"
            data-testid="sup-ship-dialog"
          >
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#C05A3C] mb-1">Mark shipped</div>
              <h3 className="font-display text-xl">{shipFor.title}</h3>
              <p className="text-xs text-muted-foreground mt-1">Buyer will be auto-emailed the tracking link via the existing shipped-email flow (carrier auto-detected).</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Supplier tracking number <span className="text-[#C05A3C]">*</span>
              </label>
              <Input
                data-testid="sup-ship-tracking"
                placeholder="e.g. LX123456789GB or 1Z999AA10123456784"
                value={trackingInput}
                onChange={(e) => setTrackingInput(e.target.value)}
                autoFocus
                required
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Carrier (optional — auto-detected)</label>
              <Input
                data-testid="sup-ship-carrier"
                placeholder="Royal Mail / DHL / UPS / CJPacket…"
                value={carrierInput}
                onChange={(e) => setCarrierInput(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShipFor(null)} disabled={busy}>Cancel</Button>
              <Button type="submit" disabled={busy || !trackingInput.trim()} className="bg-[#C05A3C] hover:bg-[#A8492F] text-white" data-testid="sup-ship-confirm">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
                Ship & notify buyer
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Row = ({ order, setShipFor, updateStatus, retryForward, autopilotEnabled }) => {
  const status = STATUS_LABELS[order.status] || STATUS_LABELS.pending_forward;
  const StatusIcon = status.icon;
  const margin = (Number(order.buyer_paid || 0) - Number(order.supplier_cost || 0)).toFixed(2);
  // Autopilot forward telemetry — surface attempts + last error for ops debugging.
  const attempts = Number(order.forward_attempts || 0);
  const lastFwdError = order.last_forward_error || null;
  const lastFwdAt = order.last_forward_at || null;
  const forwardedAt = order.forwarded_at || null;
  return (
    <tr className="border-t border-black/5 hover:bg-[#FAF8F5]/60" data-testid={`sup-row-${order.supplier_order_id}`}>
      <td className="px-4 py-3 align-top">
        <div className="flex items-start gap-3">
          {order.listing_image && (
            <img src={order.listing_image.startsWith("http") ? order.listing_image : order.listing_image} alt="" className="w-12 h-12 rounded-md object-cover border border-black/5 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-medium text-[#1A2024] text-sm line-clamp-2">{order.listing_title}</div>
            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
              <Mail className="w-3 h-3" />
              <a href={`mailto:${order.buyer_email}`} className="hover:text-[#C05A3C] truncate">{order.buyer_name || order.buyer_email}</a>
              <span>·</span>
              <span>{order.buyer_country || "?"}</span>
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 align-top text-xs">
        {order.variant_size && <div><span className="text-muted-foreground">Size:</span> <strong>{order.variant_size}</strong></div>}
        {order.variant_color && <div><span className="text-muted-foreground">Color:</span> <strong>{order.variant_color}</strong></div>}
        <div className="text-muted-foreground mt-1">Qty: <strong className="text-[#1A2024]">{order.qty || 1}</strong></div>
      </td>
      <td className="px-4 py-3 align-top text-xs">
        <div>{order.currency?.toUpperCase()} {order.buyer_paid}</div>
        {order.supplier_cost > 0 && <div className="text-[#4A5D23] font-medium mt-0.5">+£{margin}</div>}
      </td>
      <td className="px-4 py-3 align-top">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${status.color}`}>
          <StatusIcon className="w-3 h-3" /> {status.label}
        </span>
        {order.tracking_number && (
          <div className="text-[11px] text-muted-foreground mt-1 font-mono">{order.tracking_carrier ? `${order.tracking_carrier} · ` : ""}{order.tracking_number}</div>
        )}
        {/* Autopilot forward telemetry */}
        {order.status === "forwarded" && forwardedAt && (
          <div className="text-[10px] text-[#4A5D23] mt-1 flex items-center gap-1" data-testid={`sup-auto-forwarded-${order.supplier_order_id}`}>
            <Zap className="w-2.5 h-2.5" /> Auto-forwarded
          </div>
        )}
        {order.status === "pending_forward" && attempts > 0 && lastFwdError && (
          <div className="text-[10px] text-red-600 mt-1" data-testid={`sup-fwd-error-${order.supplier_order_id}`} title={lastFwdError}>
            Forward failed ({attempts}×): {lastFwdError.length > 40 ? `${lastFwdError.slice(0, 40)}…` : lastFwdError}
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-top text-right">
        <div className="flex flex-col items-end gap-1">
          {order.supplier_url && (
            <a
              href={order.supplier_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-[#C05A3C] hover:underline flex items-center gap-1"
              data-testid={`sup-supplier-link-${order.supplier_order_id}`}
            >
              <ExternalLink className="w-3 h-3" /> Supplier
            </a>
          )}
          {order.status === "pending_forward" && (
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => updateStatus(order.supplier_order_id, "forwarded")} data-testid={`sup-mark-forwarded-${order.supplier_order_id}`}>
              Mark forwarded <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          )}
          {autopilotEnabled && order.status === "pending_forward" && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px] border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23]/10"
              onClick={() => retryForward(order.supplier_order_id)}
              data-testid={`sup-retry-forward-${order.supplier_order_id}`}
              title={lastFwdError ? `Last attempt: ${lastFwdError}` : "Retry the autopilot webhook"}
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Retry forward
            </Button>
          )}
          {(order.status === "pending_forward" || order.status === "forwarded") && (
            <Button size="sm" className="h-7 text-[11px] bg-[#C05A3C] hover:bg-[#A8492F] text-white" onClick={() => setShipFor({ id: order.supplier_order_id, title: order.listing_title })} data-testid={`sup-mark-shipped-${order.supplier_order_id}`}>
              <Truck className="w-3 h-3 mr-1" /> Ship + track
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

const Kpi = ({ label, value, accent = "", active = false, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    data-testid={testid}
    className={`text-left bg-[#F3EFE9] rounded-md p-3 transition border-2 ${active ? "border-[#C05A3C]" : "border-transparent"} ${onClick ? "hover:bg-[#EDE7DD] cursor-pointer" : "cursor-default"}`}
  >
    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
    <div className={`font-display text-xl ${accent}`}>{value}</div>
  </button>
);

export default SupplierOrders;
